import React from 'react';

const EMPTY_VALUES = [undefined, null, ''];

const isEmptyValue = (value) => EMPTY_VALUES.includes(value);

export const getSelectOptionValue = (option) => {
  if (option && typeof option === 'object' && !React.isValidElement(option)) {
    return (
      option.value ??
      option.id ??
      option.user_name ??
      option.username ??
      option.accounting_account_code ??
      option.stocks_id ??
      option.code ??
      option.key
    );
  }

  return option;
};

export const getSelectOptionLabel = (option, fallbackValue) => {
  if (option && typeof option === 'object' && !React.isValidElement(option)) {
    const code =
      option.user_name ??
      option.username ??
      option.accounting_account_code ??
      option.account_code ??
      option.stocks_code ??
      option.code;
    const name =
      option.label ??
      option.name ??
      option.title ??
      option.full_name ??
      option.user_fullname ??
      option.user_full_name ??
      option.customer_name ??
      option.supplier_name ??
      option.business_name ??
      option.store_name ??
      option.stocks_name ??
      option.department_name ??
      option.position_name ??
      option.accounting_account_name;

    if (!isEmptyValue(code) && !isEmptyValue(name) && !String(name).includes(String(code))) {
      return `${code} - ${name}`;
    }

    return name ?? code ?? fallbackValue;
  }

  return option ?? fallbackValue;
};

export const normalizeSelectOption = (option) => {
  if (option && typeof option === 'object' && !React.isValidElement(option)) {
    if (Array.isArray(option.options)) {
      return {
        ...option,
        label: getSelectOptionLabel(option, option.label),
        options: option.options.map(normalizeSelectOption),
      };
    }

    const value = getSelectOptionValue(option);
    const label = getSelectOptionLabel(option, value);

    return {
      ...option,
      value,
      label,
      title: option.title ?? (typeof label === 'string' ? label : ''),
    };
  }

  return {
    value: option,
    label: option,
    title: typeof option === 'string' ? option : '',
  };
};

export const normalizeSelectOptions = (options = []) => {
  const seenValues = new Set();

  const normalizeAndDeduplicate = (optionList) =>
    (Array.isArray(optionList) ? optionList : []).reduce((result, option) => {
      const normalizedOption = normalizeSelectOption(option);

      if (Array.isArray(normalizedOption.options)) {
        const groupOptions = normalizeAndDeduplicate(normalizedOption.options);
        if (groupOptions.length > 0) {
          result.push({ ...normalizedOption, options: groupOptions });
        }
        return result;
      }

      const value = getSelectOptionValue(normalizedOption);
      if (isEmptyValue(value)) {
        result.push(normalizedOption);
        return result;
      }

      const valueKey = String(value);
      if (!seenValues.has(valueKey)) {
        seenValues.add(valueKey);
        result.push(normalizedOption);
      }

      return result;
    }, []);

  return normalizeAndDeduplicate(options);
};

const flattenOptions = (options = []) =>
  (Array.isArray(options) ? options : []).flatMap((option) =>
    Array.isArray(option?.options) ? flattenOptions(option.options) : [option],
  );

export const findSelectOption = (options = [], value) => {
  if (isEmptyValue(value)) return undefined;
  const primitiveValue = getSelectOptionValue(value);

  return flattenOptions(options).find((option) => String(getSelectOptionValue(option)) === String(primitiveValue));
};

export const toPlainSelectValue = (value, isMultiple = false) => {
  if (isMultiple) {
    return (Array.isArray(value) ? value : []).map(getSelectOptionValue).filter((item) => !isEmptyValue(item));
  }

  const primitiveValue = getSelectOptionValue(value);
  return isEmptyValue(primitiveValue) ? undefined : primitiveValue;
};

export const hasSelectValue = (value, isMultiple = false) => {
  const plainValue = toPlainSelectValue(value, isMultiple);

  if (Array.isArray(plainValue)) {
    return plainValue.length > 0;
  }

  return !isEmptyValue(plainValue);
};

const getRequiredMessage = (required) => {
  if (typeof required === 'string') {
    return required;
  }

  return required?.message || 'Trường này là bắt buộc.';
};

export const getSelectValidation = (validation, isMultiple = false) => {
  if (!validation?.required || validation.required?.value === false) {
    return validation;
  }

  const requiredMessage = getRequiredMessage(validation.required);
  const requiredSelect = (value) => hasSelectValue(value, isMultiple) || requiredMessage;
  const customValidate = validation.validate;

  if (typeof customValidate === 'function') {
    return {
      ...validation,
      validate: (...args) => {
        const requiredResult = requiredSelect(args[0]);
        if (requiredResult !== true) {
          return requiredResult;
        }

        return customValidate(...args);
      },
    };
  }

  return {
    ...validation,
    validate: {
      requiredSelect,
      ...(customValidate && typeof customValidate === 'object' ? customValidate : {}),
    },
  };
};

export const toLabeledSelectValue = (value, options = []) => {
  if (isEmptyValue(value)) return undefined;

  const primitiveValue = getSelectOptionValue(value);
  const matchedOption = findSelectOption(options, primitiveValue);
  const label = getSelectOptionLabel(value, matchedOption?.label ?? primitiveValue);

  return {
    ...(matchedOption || {}),
    ...(value && typeof value === 'object' && !React.isValidElement(value) ? value : {}),
    value: matchedOption?.value ?? primitiveValue,
    label: matchedOption?.label ?? label,
  };
};

export const toLabeledSelectValueList = (value, options = []) =>
  (Array.isArray(value) ? value : []).map((item) => toLabeledSelectValue(item, options)).filter(Boolean);

export const toStoredMultipleSelectValue = (value = [], options = []) =>
  (Array.isArray(value) ? value : []).map((item) => {
    const matchedOption = findSelectOption(options, item);
    const primitiveValue = getSelectOptionValue(matchedOption ?? item);
    const label = getSelectOptionLabel(matchedOption ?? item, primitiveValue);

    return {
      ...(matchedOption || {}),
      id: matchedOption?.id ?? primitiveValue,
      value: primitiveValue,
      label,
    };
  });
